import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../api';
import { useAuth } from './AuthContext';
import { log } from '../lib/logger';

const CMSContext = createContext();
export const useCMS = () => useContext(CMSContext);

// External effect bodies — keep hook bodies minimal.
const loadContentAndProducts = async (setContent, setProducts, setLoading) => {
  try {
    const [c, p] = await Promise.all([api.get('/content'), api.get('/products')]);
    setContent(c.data || {});
    setProducts(p.data || []);
  } catch (e) {
    log.error('CMS load error', e);
  } finally {
    setLoading(false);
  }
};

export const CMSProvider = ({ children }) => {
  const { isAuthed } = useAuth();
  const [content, setContent] = useState({});
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);

  const refresh = useCallback(
    () => loadContentAndProducts(setContent, setProducts, setLoading),
    [],
  );

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { if (!isAuthed) setEditMode(false); }, [isAuthed]);

  const t = useCallback((key, fallback = '') => {
    const v = content[key];
    return v == null ? fallback : v;
  }, [content]);

  const saveContent = useCallback(async (key, value) => {
    setContent((prev) => ({ ...prev, [key]: value }));
    try { await api.put('/content/item', { key, value }); }
    catch (e) { log.error('Save failed', e); throw e; }
  }, []);

  const updateProduct = useCallback(async (id, patch) => {
    const { data } = await api.put(`/products/${id}`, patch);
    setProducts((prev) => prev.map((p) => (p.id === id ? data : p)));
    return data;
  }, []);

  const createProduct = useCallback(async (payload) => {
    const { data } = await api.post('/products', payload);
    setProducts((prev) => [...prev, data]);
    return data;
  }, []);

  const deleteProduct = useCallback(async (id) => {
    await api.delete(`/products/${id}`);
    setProducts((prev) => prev.filter((p) => p.id !== id));
  }, []);

  // Split useMemo into independent slices so no single hook has more than
  // ~4 deps. Each slice is responsible for one concern.
  const featuredProducts = useMemo(
    () => products.filter((p) => p.featured).slice(0, 4),
    [products],
  );

  const productSlice = useMemo(
    () => ({ products, featuredProducts, updateProduct, createProduct, deleteProduct }),
    [products, featuredProducts, updateProduct, createProduct, deleteProduct],
  );

  const contentSlice = useMemo(
    () => ({ content, t, saveContent, refresh }),
    [content, t, saveContent, refresh],
  );

  const editingSlice = useMemo(
    () => ({ editMode, setEditMode, loading }),
    [editMode, loading],
  );

  const value = useMemo(
    () => ({ ...productSlice, ...contentSlice, ...editingSlice }),
    [productSlice, contentSlice, editingSlice],
  );

  return <CMSContext.Provider value={value}>{children}</CMSContext.Provider>;
};
